import React, { useEffect, useRef, useState } from "react";
import { FaMapMarkerAlt, FaCoffee, FaExclamationTriangle } from "react-icons/fa";
import api from "../../services/api";

const LocationTracker = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");
  const trackingInterval = useRef(null);
  const idleCheckInterval = useRef(null);
  const gpsWatchInterval = useRef(null);
  const gpsOffStartTime = useRef(null);
  
  const [trackerStatus, setTrackerStatus] = useState({
    status: "initializing",
    message: "Starting location tracking...",
    isInside: true,
    distance: 0,
    lat: null,
    lng: null,
    accuracy: null,
    lastUpdate: null,
    gpsEnabled: true,
  });
  const [isCheckedOut, setIsCheckedOut] = useState(false);
  const [isOutside, setIsOutside] = useState(false);
  const [isActivelyTracking, setIsActivelyTracking] = useState(false);
  const [isGpsOff, setIsGpsOff] = useState(false);

  const isBreakTime = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const totalMinutes = (hours * 60) + minutes;
    const breakStart = (13 * 60) + 30;
    const breakEnd = (14 * 60) + 0;
    return totalMinutes >= breakStart && totalMinutes <= breakEnd;
  };

  const isOutsideWorkingHours = () => {
    const now = new Date();
    const hours = now.getHours();
    return hours < 9 || hours >= 17;
  };

  const stopAllIntervals = () => {
    if (trackingInterval.current) {
      clearInterval(trackingInterval.current);
      trackingInterval.current = null;
    }
    if (idleCheckInterval.current) {
      clearInterval(idleCheckInterval.current);
      idleCheckInterval.current = null;
    }
    if (gpsWatchInterval.current) {
      clearInterval(gpsWatchInterval.current);
      gpsWatchInterval.current = null;
    }
    setIsActivelyTracking(false);
  };

  const calculateDistanceFromCoords = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  };

  // ✅ Send GPS status to server
  const sendGpsStatusToServer = async (gpsEnabled, eventTime) => {
    try {
      const response = await api.post("/attendance/gps_status", 
        { 
          gps_enabled: gpsEnabled,
          event_time: eventTime || new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log(`GPS ${gpsEnabled ? 'ON' : 'OFF'} sent to server:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Failed to send GPS ${gpsEnabled ? 'ON' : 'OFF'}:`, error.message);
      return null;
    }
  };

  // ✅ Handle GPS turned OFF
  const handleGpsTurnedOff = () => {
    if (isGpsOff) return; // Already handling GPS off
    
    console.log("GPS turned OFF - Starting outside time tracking");
    setIsGpsOff(true);
    const offTime = new Date();
    gpsOffStartTime.current = offTime;
    
    // Stop location tracking intervals
    if (trackingInterval.current) {
      clearInterval(trackingInterval.current);
      trackingInterval.current = null;
    }
    if (idleCheckInterval.current) {
      clearInterval(idleCheckInterval.current);
      idleCheckInterval.current = null;
    }
    setIsActivelyTracking(false);
    
    // Update UI
    setTrackerStatus(prev => ({
      ...prev,
      gpsEnabled: false,
      status: "gps_off",
      message: "GPS is OFF - Counting as outside time",
      isInside: false,
      lastUpdate: offTime.toLocaleTimeString()
    }));
    
    // ✅ Send GPS OFF to server
    sendGpsStatusToServer(false, offTime.toISOString());
  };

  // ✅ Handle GPS turned ON
  const handleGpsTurnedOn = () => {
    if (!isGpsOff) return; // GPS was not off
    
    console.log("GPS turned ON - Resuming normal tracking");
    
    const onTime = new Date();
    
    setIsGpsOff(false);
    gpsOffStartTime.current = null;
    
    // Update UI
    setTrackerStatus(prev => ({
      ...prev,
      gpsEnabled: true,
      status: "active",
      message: "GPS is ON - Tracking resumed",
      lastUpdate: onTime.toLocaleTimeString()
    }));
    
    // ✅ Send GPS ON to server
    sendGpsStatusToServer(true, onTime.toISOString());
    
    // ✅ Resume normal tracking
    idleLocationCheck();
    if (!idleCheckInterval.current) {
      idleCheckInterval.current = setInterval(idleLocationCheck, 300000);
    }
  };

  // ✅ Check GPS status using Permissions API
  const checkGpsStatus = () => {
    if (!navigator.permissions) {
      // Fallback: Try getting location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          () => handleGpsTurnedOn(),
          (error) => {
            if (error.code === error.PERMISSION_DENIED || 
                error.code === error.POSITION_UNAVAILABLE) {
              handleGpsTurnedOff();
            }
          },
          { timeout: 5000, maximumAge: 10000 }
        );
      }
      return;
    }

    navigator.permissions.query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'granted') {
          handleGpsTurnedOn();
        } else {
          handleGpsTurnedOff();
        }
        
        // Listen for changes
        result.onchange = () => {
          if (result.state === 'granted') {
            handleGpsTurnedOn();
          } else {
            handleGpsTurnedOff();
          }
        };
      })
      .catch(() => {
        // Fallback
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            () => handleGpsTurnedOn(),
            (error) => {
              if (error.code === error.PERMISSION_DENIED || 
                  error.code === error.POSITION_UNAVAILABLE) {
                handleGpsTurnedOff();
              }
            },
            { timeout: 5000, maximumAge: 10000 }
          );
        }
      });
  };

  // ✅ Send location to server
  const sendLocationToServer = async (lat, lng, accuracy) => {
    try {
      const response = await api.post("/attendance/track_location", 
        { lat, lng, accuracy },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;
      
      if (data.status === "already_checked_out" || data.status === "no_active_attendance") {
        setIsCheckedOut(true);
        stopAllIntervals();
        return null;
      }

      setTrackerStatus(prev => ({
        ...prev,
        status: data.status,
        message: data.message || "",
        isInside: data.is_inside,
        distance: data.distance || prev.distance,
        pauseMinutes: data.total_pause_minutes || data.current_pause_minutes || 0
      }));

      return data;
    } catch (error) {
      console.error("Track location error:", error.message);
      
      // ✅ If error due to permission, treat as GPS OFF
      if (error.response?.status === 403 || error.code === 'ERR_NETWORK') {
        handleGpsTurnedOff();
      }
      return null;
    }
  };

  // ✅ Called every 5 minutes
  const idleLocationCheck = async () => {
    if (isCheckedOut || isOutsideWorkingHours() || isBreakTime()) return;
    if (isGpsOff) return; // Don't check if GPS is off

    if (!navigator.geolocation) {
      handleGpsTurnedOff();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const officeLat = 30.661770978901735;
        const officeLng = 73.13606289934688;
        const calculatedDistance = calculateDistanceFromCoords(lat, lng, officeLat, officeLng);
        const isInsideLocal = calculatedDistance <= 180;

        setTrackerStatus(prev => ({
          ...prev,
          lat, lng,
          accuracy: position.coords.accuracy,
          distance: calculatedDistance,
          isInside: isInsideLocal,
          gpsEnabled: true,
          lastUpdate: new Date().toLocaleTimeString()
        }));

        const serverResponse = await sendLocationToServer(lat, lng, position.coords.accuracy);
        
        if (!serverResponse) return;

        if (!isInsideLocal && !isOutside) {
          console.log("🚶 User left office - Starting active tracking");
          setIsOutside(true);
          startActiveTracking();
        } else if (isInsideLocal && isOutside) {
          console.log("🏢 User returned to office - Stopping active tracking");
          setIsOutside(false);
          stopActiveTracking();
        }
      },
      (error) => {
        console.log("Idle GPS error:", error.message);
        if (error.code === error.PERMISSION_DENIED || 
            error.code === error.POSITION_UNAVAILABLE) {
          handleGpsTurnedOff();
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  // ✅ Called every 60 seconds when outside
  const activeLocationTrack = async () => {
    if (isCheckedOut) {
      stopAllIntervals();
      return;
    }
    if (isGpsOff) return;

    if (!navigator.geolocation) {
      handleGpsTurnedOff();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const officeLat = 30.661770978901735;
        const officeLng = 73.13606289934688;
        const calculatedDistance = calculateDistanceFromCoords(lat, lng, officeLat, officeLng);
        const isInsideLocal = calculatedDistance <= 180;

        setTrackerStatus(prev => ({
          ...prev,
          lat, lng,
          accuracy: position.coords.accuracy,
          distance: calculatedDistance,
          isInside: isInsideLocal,
          gpsEnabled: true,
          lastUpdate: new Date().toLocaleTimeString()
        }));

        const serverResponse = await sendLocationToServer(lat, lng, position.coords.accuracy);
        
        if (!serverResponse) return;

        if (isInsideLocal) {
          console.log("🏢 User returned - Stopping active tracking");
          setIsOutside(false);
          stopActiveTracking();
        }
      },
      (error) => {
        console.log("Active GPS error:", error.message);
        if (error.code === error.PERMISSION_DENIED || 
            error.code === error.POSITION_UNAVAILABLE) {
          handleGpsTurnedOff();
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const startActiveTracking = () => {
    if (trackingInterval.current) return;
    setIsActivelyTracking(true);
    console.log("Active tracking started (every 60s)");
    activeLocationTrack();
    trackingInterval.current = setInterval(activeLocationTrack, 60000);
  };

  const stopActiveTracking = () => {
    if (trackingInterval.current) {
      clearInterval(trackingInterval.current);
      trackingInterval.current = null;
    }
    setIsActivelyTracking(false);
    console.log("Active tracking stopped");
  };

  useEffect(() => {
    if (!user.id || user.role === 'admin') return;

    console.log("LocationTracker started with GPS monitoring");

    // ✅ Start GPS status checking
    checkGpsStatus();
    gpsWatchInterval.current = setInterval(checkGpsStatus, 30000); // Every 30 seconds

    // Initial location check
    const initialTimeout = setTimeout(() => {
      idleLocationCheck();
    }, 3000);

    // Idle check every 5 minutes
    idleCheckInterval.current = setInterval(idleLocationCheck, 300000);

    return () => {
      clearTimeout(initialTimeout);
      stopAllIntervals();
    };
  }, [user.id, token, isCheckedOut]);

  // ✅ Hide if checked out or outside working hours
  if (isCheckedOut || isOutsideWorkingHours()) {
    return null;
  }

  // ✅ Show badge with GPS status
  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      right: '10px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      alignItems: 'flex-end'
    }}>
      <div style={{
        background: isBreakTime() ? '#f59e0b' : 
                    isGpsOff ? '#dc2626' :
                    trackerStatus.isInside ? '#10b981' : '#ef4444',
        color: 'white',
        padding: '10px 16px',
        borderRadius: '24px',
        fontSize: '13px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
        opacity: 0.95,
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }} title={trackerStatus.message}>
        <span style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: isBreakTime() ? '#fff' :
                      isGpsOff ? '#fca5a5' :
                      trackerStatus.isInside ? '#fff' : '#fca5a5',
          animation: (!trackerStatus.isInside || isGpsOff) ? 'pulse 1.5s infinite' : 'none'
        }}></span>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            {isBreakTime() ? <><FaCoffee style={{ color: '#fff' }} /> Break Time</> :
             isGpsOff ? <><FaExclamationTriangle style={{ color: '#fff' }} /> GPS OFF - Outside Time</> :
             trackerStatus.isInside ? <><FaMapMarkerAlt style={{ color: '#fff' }} /> In Office</> : <><FaMapMarkerAlt style={{ color: '#fff' }} /> Outside Office</>}
          </span>
          <span style={{ fontSize: '11px', opacity: 0.8 }}>
            {isBreakTime() ? 'Tracking paused' :
             isGpsOff ? 'GPS disabled - counting outside' :
             trackerStatus.distance > 0 ? `${trackerStatus.distance}m away` : ''}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LocationTracker;